// Interfaces para a funcionalidade de % Impostos/Faturamento

// Interface para os dados de impostos calculados por empresa
export interface ITaxAnalysisCompany {
  id: number;
  external_id: string;
  razao_social: string;
  cnpj: string;
  total_faturamento: number;
  impostos: {
    pis: number;
    pis_percentual: number;
    cofins: number;
    cofins_percentual: number;
    csll: number;
    csll_percentual: number;
    irpj: number;
    irpj_percentual: number;
    icms: number;
    icms_percentual: number;
    iss: number;
    iss_percentual: number;
    iss_retido: number;
    iss_retido_percentual: number;
    total_impostos: number;
    total_percentual: number;
  };
  regime_tributario?: string;
}

// Interface para cada apuração do Simples Nacional (múltiplos anexos)
export interface IApuracaoSN {
  anexo: number;
  secao?: number | null;
  tabela?: number | null;
  valor_guia: number;
  valor_ia: number;
  faixa?: number | null;
  aliquota_efetiva?: number | null;
  info_partilha?: string | null;
}

// Interface para Simples Nacional
export interface ITaxAnalysisSNCompany {
  id: number;
  external_id: string;
  razao_social: string;
  cnpj: string;
  total_faturamento: number;
  faturamento_acumulado: number;
  vl_simples_nacional: number;
  simples_nacional_percentual: number;
  vl_saida: number;
  vl_servico: number;
  vl_outros: number;
  iss: number;
  iss_retido: number;
  anexo_simples_nacional?: number;
  secao_simples_nacional?: number;
  tabela_simples_nacional?: number;
  apuracoes?: IApuracaoSN[];
}

// Interface para totais agregados
export interface ITaxAnalysisTotals {
  total_empresas: number;
  total_faturamento: number;
  total_pis: number;
  total_cofins: number;
  total_csll: number;
  total_irpj: number;
  total_icms: number;
  total_impostos: number;
  media_percentual: number;
}

export type ITaxAnalysisCSVResponse =
  | ITaxAnalysisCompany[]
  | {
      error: string;
      message?: string;
    };

// Interface para filtros de análise de impostos (Lucro Real/Presumido)
export interface ITaxAnalysisFilters {
  date_start?: string;
  date_end?: string;
  page?: string;
  size?: string;
  screen_data?: 'fiscal' | 'simples_nacional';
  tipo_regime?: string;
  // tipo_regime?: 'lucro_presumido' | 'lucro_real' | 'simples_nacional';
  external_id?: string;
  cnpj?: string;
  razao_social?: string;
  cidade?: string;
  uf?: string;
  grupo_de_empresas?: string;
  tags?: string;
  comercial?: string;
  contabil?: string;
  financeiro?: string;
  contas_a_pagar?: string;
  encerramento_anual?: string;
  fiscal?: string;
  imposto_de_renda?: string;
  integracao_contabil?: string;
  integracao_fiscal?: string;
  pessoal?: string;
  relacionamento_com_cliente?: string;
  societario?: string;
  order_by?: string;
}

// Interface para filtros de análise Simples Nacional
export interface ITaxAnalysisSNFilters extends ITaxAnalysisFilters {
  screen_data?: 'simples_nacional';
}

// Interface para historico mensal (graficos)
export interface ITaxAnalysisHistory {
  mes: string; // "2024-01"
  total_faturamento: number;
  pis: number;
  pis_percentual: number;
  cofins: number;
  cofins_percentual: number;
  csll: number;
  csll_percentual: number;
  irpj: number;
  irpj_percentual: number;
  icms: number;
  icms_percentual: number;
  iss: number;
  iss_percentual: number;
  iss_retido: number;
  iss_retido_percentual: number;
  total_impostos: number;
  total_percentual: number;
}

// Interface para historico mensal de Simples Nacional (graficos)
export interface ITaxAnalysisSNHistory {
  mes: string; // "2024-01"
  total_faturamento: number;
  vl_simples_nacional: number;
  simples_nacional_percentual: number;
  vl_saida: number;
  vl_servico: number;
  vl_outros: number;
}

// Interface para dados do grafico de pizza
export interface ITaxPieChartData {
  name: string; // "PIS", "COFINS", etc
  value: number;
  percentual: number;
  color: string;
}

// Funcoes utilitarias para calculos de percentuais
export const calculateTaxPercentage = (
  taxValue: number | null | undefined,
  totalRevenue: number | null | undefined,
): number => {
  if (!totalRevenue || totalRevenue === 0 || !taxValue) return 0;
  return Number(((taxValue / totalRevenue) * 100).toFixed(2));
};

export const calculateTotalTaxes = (impostos: {
  pis?: number | null;
  cofins?: number | null;
  csll?: number | null;
  irpj?: number | null;
  icms?: number | null;
}): number => {
  return (
    (impostos.pis || 0) +
    (impostos.cofins || 0) +
    (impostos.csll || 0) +
    (impostos.irpj || 0) +
    (impostos.icms || 0)
  );
};

// Formatacao monetaria
export const formatarEmReais = (valor: number | null | undefined): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valor ?? 0);
};

// Formatacao percentual
export const formatarPercentual = (valor: number | null | undefined): string => {
  return `${(valor ?? 0).toFixed(2)}%`;
};

// Cores para os impostos nos graficos
export const TAX_COLORS = {
  PIS: '#8884d8',
  COFINS: '#82ca9d',
  CSLL: '#ffc658',
  IRPJ: '#ff8042',
  ICMS: '#0088fe',
  ISS: '#2600ff',
  SIMPLES_NACIONAL: '#4CAF50',
};
